import { FC, useEffect, useState } from "react";
import { Accordion } from "../../../components/controls/accordion"
import { Checkbox } from 'antd';
import { v4 as uuidv4} from 'uuid';
import FlexColumn from "../../../components/controls/FlexColumn";
import { NumberInput } from "../../../components/controls/inputs/BaseInput";
import styles from "../AnalysisLot/Styles/AnalysisFilterLot.module.scss"
import { useTranslation } from "react-i18next";
import { useLocalStorageChange } from "../../../system/hooks/useLocalStorageChange";
import { IJournalFilters, ISalesReport, typeReport } from "../DashboardBusinessControls/interfaces";
import { IOption, Select } from "../../../components/selects/select";
import FlexRow from "../../../components/controls/FlexRow";

interface Props {
    onOpenAccordion: (name: TypeAccordionName) => void;
    setFilters:(filters: IAnalysisFilters) => void;
    onFiltersLoaded: () => void;
    currentAccordion?: TypeAccordionName;
    tabId?: string;    
}

export type TypeAccordionName = 'salesReport' | 'none'

export interface IAnalysisFilters {    
    IsGnvls?: boolean,
    salesReport: ISalesReport
}

export const defaultAnalysisFilters = {
    salesReport: {
        PeriodInDays: 30,
        LflPercentage: 5,
        IncludeCheques: true,
        IncludeMovements: false,
        IncludeInvoiceOut: false,
        CompareSalesCount: {displayName: '>=', value: 'MoreOrEq' as OperatorType}
    }
} as IAnalysisFilters

export const AnalyticalFilters: FC<Props> = ({
    onOpenAccordion,
    setFilters,
    onFiltersLoaded,
    tabId
}) => {
    const [activeAccordion, setActiveAccordion] = useState<TypeAccordionName>('none');
    const [savedFilters, setSavedFilters] = useLocalStorageChange<IJournalFilters>('journalFilters');  
    
    const processFilters = (filters: IJournalFilters) => {
        try {
            const updatedFilters = { ...defaultAnalysisFilters };
            let accordionToOpen: TypeAccordionName = 'none';

            updatedFilters.IsGnvls = filters.filters.IsGnvls;
            
            if (filters.reportType === typeReport.SalesReport) {
                updatedFilters.salesReport = {
                    ...updatedFilters.salesReport,                    
                    PeriodInDays: Number(filters.filters?.DashboardSalesReport?.PeriodInDays) || defaultAnalysisFilters.salesReport.PeriodInDays,
                    LflPercentage: Number(filters.filters?.DashboardSalesReport?.LflPercentage) || defaultAnalysisFilters.salesReport.LflPercentage,
                    IncludeCheques: Boolean(filters.filters.DashboardSalesReport?.IncludeCheques) || defaultAnalysisFilters.salesReport.IncludeCheques,
                    IncludeMovements: Boolean(filters.filters.DashboardSalesReport?.IncludeMovements) || defaultAnalysisFilters.salesReport.IncludeMovements,
                    IncludeInvoiceOut: Boolean(filters.filters.DashboardSalesReport?.IncludeInvoiceOut) || defaultAnalysisFilters.salesReport.IncludeInvoiceOut,
                    CompareSalesCount: {
                        displayName:filters.filters?.DashboardSalesReport?.CompareSalesCount?.displayName ?? ">=",
                        value : filters.filters?.DashboardSalesReport?.CompareSalesCount?.value ?? "MoreOrEq"
                    },
                };
                accordionToOpen = 'salesReport';
                setActiveAccordion('salesReport');
            }
            setAnalysisFilters(updatedFilters);
            setFilters(updatedFilters);
            onOpenAccordion(accordionToOpen);
            setSavedFilters(null);
            localStorage.removeItem('journalFilters');
            onFiltersLoaded();
        } catch (error) {
            console.error(error);
        }
    };

    const defaultSearchOptions = [
        {displayName: '=', value: 'Eq' as OperatorType},
        {displayName: '>', value: 'More' as OperatorType},
        {displayName: '>=', value: 'MoreOrEq' as OperatorType},
        {displayName: '<', value: 'Less' as OperatorType},
        {displayName: '<=', value: 'LessOrEq' as OperatorType},
    ] as IOption[];
    
    const [analysisFilters, setAnalysisFilters] = useState<IAnalysisFilters>(defaultAnalysisFilters);
    const { t } = useTranslation();
    const baseT = (value: string) => t('analysisLot.' + value)

    const handleAccordionToggle = (name: TypeAccordionName, isOpen: boolean) => {
        setActiveAccordion(isOpen ? name : 'none');
        onOpenAccordion(isOpen ? name : 'none');
    };

    const handleCheckBoxChange = (e: { target: { checked: boolean } }, filterName: string) => {
        const checked = e.target.checked;
        
        const newFilters = {
            ...analysisFilters,
            salesReport: {
                ...analysisFilters.salesReport,
                [filterName]: checked
            }
        };
        
        setAnalysisFilters(newFilters);
        setFilters(newFilters);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(analysisFilters);
        }, 200);
        return () => clearTimeout(timer);
    }, [analysisFilters]);

    useEffect(() => {
        if (!savedFilters || !tabId) return;

        if (savedFilters.tabId === tabId) {
            processFilters(savedFilters);           
            setSavedFilters(null);
        }
    }, [savedFilters, tabId]);

    return <>
        <h1>{baseT('analysisSales')}</h1>
        <Accordion 
            opened={activeAccordion === 'salesReport'}
            caption={baseT('likeForLike')} 
            id={`${uuidv4()}`}
            onOpen={(isOpen) => handleAccordionToggle('salesReport', isOpen)}        
        >
            <FlexColumn className={styles.flexColumn}>
                <NumberInput
                    className={styles.numberInput} label={baseT('periodInDays')}
                    value={analysisFilters.salesReport.PeriodInDays}
                    min={0}
                    onChange={(value) => setAnalysisFilters({ ...analysisFilters, salesReport: { ...analysisFilters.salesReport, PeriodInDays: value } })}
                />
                <FlexRow>
                    <NumberInput
                        className={styles.numberInput} label={baseT('lflFilter')}
                        value={analysisFilters.salesReport.LflPercentage}
                        min={0}
                        onChange={(value) => setAnalysisFilters({ ...analysisFilters, salesReport: { ...analysisFilters.salesReport, LflPercentage: value } })}
                    />
                    <Select
                        label={baseT('compare')}
                        defaultOption={defaultSearchOptions[2]}
                        value={analysisFilters.salesReport.CompareSalesCount}
                        options={defaultSearchOptions}
                        onSelect={(option) => setAnalysisFilters({ ...analysisFilters, salesReport: { ...analysisFilters.salesReport, CompareSalesCount: option } })}
                    />
                </FlexRow>
                <FlexColumn>
                    <div className={styles.includeBlockContainer}>
                        <div className={styles.includeBlockTitle}>{baseT('includeBlockTitle')}</div>
                        <div className={styles.includeBlockCheckBoxWrapper}>
                            <Checkbox
                                style={{ marginLeft: 0 }}
                                name="IncludeCheques"
                                className={styles.customCheckbox}
                                checked={analysisFilters.salesReport.IncludeCheques}
                                onChange={(e) => handleCheckBoxChange(e, 'IncludeCheques')}
                            >
                                {baseT('IncludeCheques')}
                            </Checkbox>
                            <Checkbox
                                style={{ marginLeft: 0 }}
                                name="IncludeMovements"
                                className={styles.customCheckbox}
                                checked={analysisFilters.salesReport.IncludeMovements}
                                onChange={(e) => handleCheckBoxChange(e, 'IncludeMovements')}
                            >
                                {baseT('IncludeMovements')}
                            </Checkbox>
                            <Checkbox
                                style={{ marginLeft: 0 }}
                                name="IncludeInvoiceOut"
                                className={styles.customCheckbox}
                                checked={analysisFilters.salesReport.IncludeInvoiceOut}
                                onChange={(e) => handleCheckBoxChange(e, 'IncludeInvoiceOut')}
                            >
                                {baseT('IncludeInvoiceOut')}
                            </Checkbox>
                        </div>
                    </div>
                </FlexColumn>
            </FlexColumn>
        </Accordion>
    </>
}